import { createRef, FunctionComponent, ReactElement, RefObject, useEffect } from "react";

import { FakeComponentProps } from "@multiweb/reactComponents";
import { MultiEditProps } from "../types";

export type MultiEditWrapperChildrenProps = {
    /** Reference na reálnou komponentu. Příklad použití je v komponentě Edit.tsx. */
    ref: RefObject<any>;
};

export type MultiEditWrapperFakeComponentRendererProps = Pick<FakeComponentProps, "disabled">;

export type MultiEditWrapperProps = {
    /** Příznak, zda je pole zakázáno */
    disabled?: boolean;
    /** Příznak, zda je pole v módu editace */
    isEditing?: boolean;
    /** Funkce pro vykreslení fake komponenty. Využívá techniku render props pro možnost předání dodatečných props. */
    fakeComponentRenderer: (props: MultiEditWrapperFakeComponentRendererProps) => ReactElement;
    /** Příznak, zda proběhla interakce s fake komponentou v rámci jednoho cyklu hromadné editace. */
    touched: boolean;
    /** Reálná komponenta, která se vykresluje, když není aktivní multi edit. */
    children: (props: MultiEditWrapperChildrenProps) => ReactElement;
} & MultiEditProps;

/**
 * Pomocná komponenta pro přepínání fake komponent pro hromadnou editaci.
 *
 * Pokud je multieditace aktivní, je zobrazena fake komponenta skrze `fakeComponentRenderer`.
 * Pro spuštění editace je nejprve nutné kliknout na fake komponentu, která na ni nastaví focus.
 * Při následném stisknutí klávesy `Backspace` nebo `Delete` se fake komponenta nahradí obalovanou komponentou a režim editace je aktivní.
 */
export const MultiEditWrapper: FunctionComponent<MultiEditWrapperProps> = ({
    multiEditActive = false,
    disabled = false,
    isEditing = false,
    fakeComponentRenderer,
    touched,
    children,
}) => {
    const ref = createRef<HTMLElement>();

    useEffect(() => {
        if (touched && ref.current) {
            ref.current.focus();
        }
        // TODO: 37450 - Reference se mění při každé změně hodnoty. Pokud se hodnota nezmění na touch, tak NumEdit neumožní
        // přepnutí na jiný edit. Pokud referenci z dependencies odstraníme, vše funguje jak má.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [touched]);

    const showFakeComponent = multiEditActive && (!touched || !isEditing);

    return showFakeComponent ? (
        fakeComponentRenderer({ disabled })
    ) : (
        children({ ref })
    );
};
